import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { IamMeResponse, IamUiCapabilities, IamUserService } from './iam-user.service';

/** Screen codes granted when a parent Product/Section code is present in iam_user_access. */
const ACCESS_CODE_CHILDREN: Record<string, string[]> = {
  REPORTING: [
    'REPORTING_AMAZON_VENDOR',
    'REPORTING_AMAZON_VENDOR_GLOBAL_INSIGHT',
    'REPORTING_AMAZON_VENDOR_SELL_IN',
    'REPORTING_AMAZON_VENDOR_SELL_OUT',
    'REPORTING_AMAZON_VENDOR_FORECAST',
    'REPORTING_AMAZON_VENDOR_INVOICE',
    'REPORTING_AMAZON_VENDOR_INVENTORY',
    'REPORTING_AMAZON_SELLER',
    'REPORTING_AMAZON_SELLER_ORDERS',
    'REPORTING_AMAZON_ADS',
    'REPORTING_AMAZON_ADS_PERFORMANCE_VIEW',
    'REPORTING_AMAZON_ADS_PRODUCT_VIEW',
    'REPORTING_AMAZON_CA',
    'REPORTING_AMAZON_CA_SHORTAGE',
    'REPORTING_AMAZON_CA_PRICE',
    'REPORTING_AMAZON_CA_NON_COMPLIANCE',
    'REPORTING_AMAZON_CA_RETURNS',
    'MASTER_FILE',
    'REPORTING_DATA_AGENTS',
  ],
  REPORTING_AMAZON_VENDOR: [
    'REPORTING_AMAZON_VENDOR_GLOBAL_INSIGHT',
    'REPORTING_AMAZON_VENDOR_SELL_IN',
    'REPORTING_AMAZON_VENDOR_SELL_OUT',
    'REPORTING_AMAZON_VENDOR_FORECAST',
    'REPORTING_AMAZON_VENDOR_INVOICE',
    'REPORTING_AMAZON_VENDOR_INVENTORY',
  ],
  REPORTING_AMAZON_SELLER: ['REPORTING_AMAZON_SELLER_ORDERS'],
  REPORTING_AMAZON_ADS: [
    'REPORTING_AMAZON_ADS_PERFORMANCE_VIEW',
    'REPORTING_AMAZON_ADS_PRODUCT_VIEW',
  ],
  REPORTING_AMAZON_CA: [
    'REPORTING_AMAZON_CA_SHORTAGE',
    'REPORTING_AMAZON_CA_PRICE',
    'REPORTING_AMAZON_CA_NON_COMPLIANCE',
    'REPORTING_AMAZON_CA_RETURNS',
  ],
  APP_MANAGEMENT: [
    'APP_TENANTS_MANAGEMENT',
    'APP_AMAZON_AUTHENTIFICATION',
    'APP_CLIENT_MANAGEMENT',
    'APP_USER_MANAGEMENT',
  ],
  CATALOG_MANAGEMENT: [
    'CATALOG_LISTING_MANAGEMENT',
    'CATALOG_CATEGORIES_MANAGEMENT',
    'CATALOG_PRODUCT_MONITORING',
    'CATALOG_AMZ_VENDOR_FRONT',
    'CATALOG_AMZ_VENDOR_FRONT_PRODUCT_MONITORING',
    'CATALOG_AMZ_VENDOR_FRONT_CHECK_STORE',
    'CATALOG_AMZ_VENDOR_FRONT_ADVANCED_CHECK_STORE',
    'CATALOG_SCRAPING_BACK_OFFICE',
    'CATALOG_ABS_BACK_OFFICE',
    'CATALOG_ABS_GPSR',
  ],
  CATALOG_AMZ_VENDOR_FRONT: [
    'CATALOG_AMZ_VENDOR_FRONT_PRODUCT_MONITORING',
    'CATALOG_AMZ_VENDOR_FRONT_CHECK_STORE',
    'CATALOG_AMZ_VENDOR_FRONT_ADVANCED_CHECK_STORE',
  ],
  CATALOG_SCRAPING_BACK_OFFICE: ['CATALOG_ABS_BACK_OFFICE', 'CATALOG_ABS_GPSR'],
};

const HOME_SCREENS: { code: string; path: string }[] = [
  { code: 'REPORTING_AMAZON_VENDOR_GLOBAL_INSIGHT', path: '/dashboard/vendor/global-insight' },
  { code: 'REPORTING_AMAZON_VENDOR_SELL_IN', path: '/dashboard/vendor/sell-in' },
  { code: 'REPORTING_AMAZON_VENDOR_SELL_OUT', path: '/dashboard/vendor/sell-out' },
  { code: 'REPORTING_AMAZON_VENDOR_FORECAST', path: '/dashboard/vendor/forecast' },
  { code: 'REPORTING_AMAZON_VENDOR_INVOICE', path: '/dashboard/vendor/invoice' },
  { code: 'REPORTING_AMAZON_VENDOR_INVENTORY', path: '/dashboard/vendor/Inventory' },
  { code: 'REPORTING_AMAZON_SELLER_ORDERS', path: '/dashboard/seller/orders' },
  { code: 'REPORTING_AMAZON_ADS_PERFORMANCE_VIEW', path: '/dashboard/advertising/performance-view' },
  { code: 'REPORTING_AMAZON_ADS_PRODUCT_VIEW', path: '/dashboard/advertising/product-view' },
  { code: 'REPORTING_AMAZON_CA_SHORTAGE', path: '/dashboard/accounting-claim/under-construction' },
  { code: 'REPORTING_AMAZON_CA_PRICE', path: '/dashboard/accounting-claim/under-construction' },
  { code: 'REPORTING_AMAZON_CA_NON_COMPLIANCE', path: '/dashboard/accounting-claim/under-construction' },
  { code: 'REPORTING_AMAZON_CA_RETURNS', path: '/dashboard/accounting-claim/under-construction' },
  { code: 'MASTER_FILE', path: '/dashboard/operation-management/masterfile' },
  { code: 'REPORTING_DATA_AGENTS', path: '/dashboard/operation-management/etail-data-agents' },
  { code: 'CATALOG_LISTING_MANAGEMENT', path: '/dashboard/operation-management/manage-catalog' },
  { code: 'CATALOG_CATEGORIES_MANAGEMENT', path: '/dashboard/operation-management/category-management' },
  { code: 'CATALOG_PRODUCT_MONITORING', path: '/dashboard/operation-management/product-monitoring' },
  { code: 'CATALOG_AMZ_VENDOR_FRONT_PRODUCT_MONITORING', path: '/dashboard/operation-management/amz-vendor-front-check-store/product-monitoring' },
  { code: 'CATALOG_AMZ_VENDOR_FRONT_CHECK_STORE', path: '/dashboard/operation-management/amz-vendor-front-check-store/check-store' },
  { code: 'CATALOG_AMZ_VENDOR_FRONT_ADVANCED_CHECK_STORE', path: '/dashboard/operation-management/amz-vendor-front-check-store/advanced-check-store' },
  { code: 'CATALOG_ABS_BACK_OFFICE', path: '/dashboard/operation-management/scrapping-amz-back-office?tab=back-office' },
  { code: 'CATALOG_ABS_GPSR', path: '/dashboard/operation-management/scrapping-amz-back-office?tab=gpsr' },
  { code: 'APP_TENANTS_MANAGEMENT', path: '/tenant-management' },
  { code: 'APP_AMAZON_AUTHENTIFICATION', path: '/dashboard/seller/auth-seller' },
  { code: 'APP_USER_MANAGEMENT', path: '/user-management' },
  { code: 'APP_CLIENT_MANAGEMENT', path: '/client-management' },
];

/**
 * Single FE entry point for IAM checks (menus, routes, page actions).
 * Source: GET /v1/iam/me → accessCodes from iam_user_access.
 */
@Injectable({ providedIn: 'root' })
export class IamUiPolicyService {
  private readonly meSubject = new BehaviorSubject<IamMeResponse | null>(null);
  private load$: Observable<IamMeResponse> | null = null;

  readonly me$ = this.meSubject.asObservable();

  constructor(
    private readonly iamUserService: IamUserService,
    private readonly router: Router
  ) {}

  get capabilities(): IamUiCapabilities | null {
    return this.meSubject.value?.capabilities ?? null;
  }

  get me(): IamMeResponse | null {
    return this.meSubject.value;
  }

  get accessCodes(): string[] {
    return this.meSubject.value?.accessCodes ?? [];
  }

  ensureLoaded(): Observable<IamMeResponse> {
    if (this.meSubject.value) {
      return of(this.meSubject.value);
    }
    if (!this.load$) {
      this.load$ = this.iamUserService.getMe().pipe(
        tap((me) =>
          this.meSubject.next({
            ...me,
            accessCodes: this.expandAccessCodes(me.accessCodes ?? []),
            scopedClientIds: me.scopedClientIds ?? [],
          })
        ),
        shareReplay(1),
        catchError((error) => {
          this.load$ = null;
          throw error;
        })
      );
    }
    return this.load$;
  }

  /**
   * Unique permission check for Product / Section / Screen codes from iam_user_access
   * (already filtered to active catalog codes by GET /v1/iam/me).
   * Parent Product/Section codes are expanded to child Screens on load.
   */
  canAccess(code: string): boolean {
    if (!code) {
      return false;
    }
    return this.accessCodes.includes(code);
  }

  canAccessAny(codes: string[]): boolean {
    return codes.some((code) => this.canAccess(code));
  }

  /** First accessible entry after login (Screens → App Management → capabilities). */
  resolveHomePath(): string | null {
    const fromCodes = HOME_SCREENS.find((screen) => this.canAccess(screen.code))?.path;
    if (fromCodes) {
      return fromCodes;
    }
    // Legacy / incomplete iam_user_access: still land somewhere useful.
    if (this.canAccessModule()) {
      return '/user-management';
    }
    if (this.canAccessClientManagement()) {
      return '/client-management';
    }
    return null;
  }

  /** Clear cached /me (call on logout / before re-login). */
  clear(): void {
    this.load$ = null;
    this.meSubject.next(null);
  }

  canActivateByAccessCode(accessCode: string): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map(() => {
        if (this.canAccess(accessCode)) {
          return true;
        }
        this.router.navigate(['/error/403']);
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/error/403']);
        return of(false);
      })
    );
  }

  canActivateAnyAccessCode(accessCodes: string[]): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map(() => {
        if (this.canAccessAny(accessCodes)) {
          return true;
        }
        this.router.navigate(['/error/403']);
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/error/403']);
        return of(false);
      })
    );
  }

  canActivateActivities(): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map((me) => {
        if (me.capabilities.canViewActivities) {
          return true;
        }
        this.router.navigate(['/error/403']);
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/error/403']);
        return of(false);
      })
    );
  }

  canActivateModule(): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map((me) => {
        if (me.capabilities.canAccessModule) {
          return true;
        }
        this.router.navigate(['/error/403']);
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/error/403']);
        return of(false);
      })
    );
  }

  canActivateClientManagement(): Observable<boolean> {
    return this.ensureLoaded().pipe(
      map((me) => {
        if (me.capabilities.canAccessClientManagement) {
          return true;
        }
        this.router.navigate(['/error/403']);
        return false;
      }),
      catchError(() => {
        this.router.navigate(['/error/403']);
        return of(false);
      })
    );
  }

  canViewActivities(): boolean {
    return this.capabilities?.canViewActivities ?? false;
  }

  canAccessModule(): boolean {
    return this.capabilities?.canAccessModule ?? false;
  }

  canAccessClientManagement(): boolean {
    return this.capabilities?.canAccessClientManagement ?? false;
  }

  canCreateClient(): boolean {
    return this.capabilities?.canCreateClient ?? false;
  }

  canDeleteClient(): boolean {
    return this.capabilities?.canDeleteClient ?? false;
  }

  canDeactivateClient(): boolean {
    return this.capabilities?.canDeactivateClient ?? false;
  }

  refresh(): Observable<IamMeResponse> {
    this.clear();
    return this.ensureLoaded();
  }

  /**
   * Expand Product/Section codes into children.
   * Backend GET /v1/iam/me already expands parent grants to active catalog descendants only.
   * If a parent is stored alongside only some children (stale partial grant),
   * do NOT invent the missing children — that caused EXT users with Listing+Categories
   * to also unlock INT-only Catalog sections via CATALOG_MANAGEMENT, and would
   * re-introduce screens deactivated via iam_access_catalog.is_active = false.
   */
  private expandAccessCodes(codes: string[]): string[] {
    const expanded = new Set(codes);
    for (const code of codes) {
      const children = ACCESS_CODE_CHILDREN[code];
      if (!children?.length) {
        continue;
      }
      const presentChildren = children.filter((child) => expanded.has(child));
      if (presentChildren.length === 0) {
        // Parent-only: trust /me (active descendants already expanded server-side).
        continue;
      }
      if (presentChildren.length < children.length) {
        // Partial children + parent → keep explicit children only
        expanded.delete(code);
      }
    }
    return [...expanded];
  }
}
